import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type {
  LocationResponse,
  CreateLocationRequest,
  UpdateLocationRequest,
  CreateMaintenancePeriodRequest,
  MaintenancePeriodResponse,
} from '@sportschool/shared-types';

/** Query options of `useLocations` — shared with the offline prefetch so the cache keys cannot drift. */
export function locationsQueryOptions(schoolId: string) {
  return queryOptions({
    queryKey: ['locations', schoolId],
    queryFn: async (): Promise<LocationResponse[]> => {
      const res = await api.get(`/schools/${schoolId}/locations`);
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useLocations(schoolId: string) {
  return useQuery(locationsQueryOptions(schoolId));
}

export function useCreateLocation(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateLocationRequest) => {
      const res = await api.post(`/schools/${schoolId}/locations`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', schoolId] });
      toast.success('Location created');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to create location', code, args);
    },
  });
}

export function useUpdateLocation(schoolId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateLocationRequest) => {
      const res = await api.put(`/schools/${schoolId}/locations/${locationId}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', schoolId] });
      toast.success('Location updated');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to update location', code, args);
    },
  });
}

export function useDeleteLocation(schoolId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/schools/${schoolId}/locations/${locationId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', schoolId] });
      toast.success('Location deleted');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to delete location', code, args);
    },
  });
}

// ---- Location Maintenance Date Locks ----

export function useLocationMaintenance(schoolId: string, locationId: string) {
  return useQuery<MaintenancePeriodResponse[]>({
    queryKey: ['location-maintenance', schoolId, locationId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/locations/${locationId}/maintenance`);
      return res.data;
    },
    enabled: !!schoolId && !!locationId,
  });
}

export function useCreateLocationMaintenance(schoolId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateMaintenancePeriodRequest) => {
      const res = await api.post(`/schools/${schoolId}/locations/${locationId}/maintenance`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['location-maintenance', schoolId, locationId] });
      toast.success('Blokada została dodana pomyślnie', 'toasts.maintenance_added');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail || 'Nie udało się dodać blokady';
      const code = data?.code || data?.Code || 'toasts.maintenance_add_failed';
      const args = data?.args || data?.Args;
      toast.error(detail, code, args);
    },
  });
}

export function useDeleteLocationMaintenance(schoolId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (periodId: string) => {
      await api.delete(`/schools/${schoolId}/locations/${locationId}/maintenance/${periodId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['location-maintenance', schoolId, locationId] });
      toast.success('Blokada została usunięta pomyślnie', 'toasts.maintenance_deleted');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail || 'Nie udało się usunąć blokady';
      const code = data?.code || data?.Code || 'toasts.maintenance_delete_failed';
      const args = data?.args || data?.Args;
      toast.error(detail, code, args);
    },
  });
}
